import { Box, Paper, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ConstantContext } from "components/context/ConstantProvider";
import { Form, FormContext } from "components/shared/Form";
import { buildGenericModelForm } from "components/shared/GenericModelForm";
import FooterFormDates from "components/ui/atoms/FooterFormDates";
import FormButtons from "components/ui/atoms/FormButtons";
import CommentSummaryField from "components/ui/atoms/formFields/CommentSummaryField";
import CompanyField from "components/ui/atoms/formFields/CompanyField";
import DictionaryField from "components/ui/atoms/formFields/DictionaryField";
import PeopleField from "components/ui/atoms/formFields/PeopleField";
import RHFCheckbox from "components/ui/atoms/formFields/RHFCheckbox";
import RHFTextField from "components/ui/atoms/formFields/RHFTextField";
import TagField from "components/ui/atoms/formFields/TagField";
import TeamSourceField from "components/ui/atoms/formFields/TeamSourceField";
import UserField from "components/ui/atoms/formFields/UserField";
import CompanyViewAsCard from "models/companies/CompanyViewAsCard";
import FinancialsWidget from "models/companies/FinancialsWidget";
import DealFormHeader from "models/deals/DealFormHeader";
import {
  useCreateDeal,
  useDeal,
  useDeleteDeal,
  useUpdateDeal,
} from "models/deals/apiDeal";
import { useContext } from "react";
import * as yup from "yup";
import useNeedASponsor from "./useNeedASponsor";

const StyledExtraActionTitle = styled(Box)(
  ({ theme }) => `
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight[500]};
  color: ${theme.palette_custom.darkBlue};
  text-decoration: underline;
`,
);

const StyledExtraAction = styled(Box)(
  ({ theme }) => `
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight[400]};
  color: ${theme.palette_custom.darkBlue};
  white-space: pre-line;
`,
);

const dealValidationSchema = yup.object({
  company: yup
    .number()
    .typeError("Please provide a company")
    .required("Please provide a company")
    .positive()
    .integer(),
  deal_owner: yup.string().required("Please provide an owner"),
  secondary_owner: yup.string().nullable(),
  deal_flow: yup.number().nullable(true).required("Please provide a dealflow"),
  investment_status: yup.string().required(),
  reference_revenue: yup
    .number()
    .nullable(true)
    .transform((_, val) => (val ? Number(val) : null)), // Transform "" to null instead of Nan
  reference_revenue_year: yup
    .number()
    .min(1990)
    .max(2100)
    .nullable(true)
    .transform((_, val) => (val ? Number(val) : null)), // Transform "" to null instead of Nan
  requested_amount: yup
    .number()
    .positive()
    .nullable(true)
    .transform((_, val) => (val ? Number(val) : null)), // Transform "" to null instead of Nan
  investment_round: yup.string(),
  profitable: yup.boolean().nullable(),
  majority_owned_by_founders: yup.boolean().nullable(),
  woman_among_founders: yup.string().required(),
  has_fundraiser: yup.boolean().nullable(),
  intermediary: yup.string().nullable(),
  team_sources_ids: yup.array().nullable(),
  deal_source: yup.string(),
  contributor_people: yup.array().nullable(),
  contributor_people_detail: yup.array().nullable(),
  comment: yup.string().nullable(),
  comment_summary: yup.string().nullable(),
  comment_investment: yup.string().nullable(),
  reason_to_close: yup.string().nullable(),
  reason_to_close_tag: yup.array().nullable(),
});

const getInitialValues = (object) => {
  return {
    id: object?.id || null,
    company: object?.company || null,
    company_detail: object?.company_detail || null,
    deal_owner: object?.deal_owner || "",
    deal_owner_detail: object?.deal_owner_detail || null,
    secondary_owner: object?.secondary_owner || "",
    secondary_owner_detail: object?.secondary_owner_detail || null,
    deal_flow: object?.deal_flow || "",
    deal_flow_detail: object?.deal_flow_detail || null,
    investment_status: object?.investment_status || 1,
    reference_revenue: object?.reference_revenue || "",
    reference_revenue_year: object?.reference_revenue_year || "",
    requested_amount: object?.requested_amount || "",
    investment_round: object?.investment_round || "",
    profitable: object?.profitable === undefined ? null : object?.profitable,
    majority_owned_by_founders:
      object?.majority_owned_by_founders === undefined
        ? null
        : object?.majority_owned_by_founders,
    woman_among_founders: object?.woman_among_founders || "NOT_AVAILABLE",
    has_fundraiser: object?.has_fundraiser || null,
    intermediary: object?.intermediary || "",
    team_sources_ids: object?.team_sources_ids || [],
    deal_source: object?.deal_source || "",
    contributor_people: object?.contributor_people || [],
    contributor_people_detail: object?.contributor_people_detail || [],
    comment: object?.comment || "",
    comment_summary: object?.comment_summary || "",
    comment_investment: object?.comment_investment || "",
    reason_to_close: object?.reason_to_close || "",
    reason_to_close_tag: (object?.reason_to_close_tag || []).sort(),
  };
};

const DealFormFields = () => {
  const {
    users,
    dealFlows,
    dealStatuses,
    dealInvestmentRounds,
    dealWomanAmongFounders,
    dealSources,
  } = useContext(ConstantContext);

  const {
    form: { watch },
    object: deal,
    formType,
    onCancel,
    initialValues,
  } = useContext(FormContext);

  const has_fundraiser = watch("has_fundraiser");
  const company = watch("company");
  const investment_status = watch("investment_status");
  const dealFlowId = watch("deal_flow");
  const deal_owner = watch("deal_owner");
  const secondary_owner = watch("secondary_owner");

  const need_a_sponsor = useNeedASponsor({
    investment_status,
    dealFlowId,
    deal_owner,
    secondary_owner,
  });

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "auto",
        width: "inherit",
        maxWidth: "inherit",
      }}
    >
      <DealFormHeader formType={formType} onClose={onCancel} deal={deal} />
      {/* the "70px" should be calculated with appBarHeight from context */}
      <Stack spacing={2} p={2} pt="70px">
        {company && <CompanyViewAsCard companyId={company} />}
        <CompanyField name="company" label="Company" creatable />
        <UserField name="deal_owner" label="Owner" />
        <UserField name="secondary_owner" label="Secondary Owner" />
        {need_a_sponsor && (
          <Typography sx={{ color: "error.main" }}>
            You need a sponsor on this deal.
          </Typography>
        )}
        <DictionaryField
          optionsDictionary={Object.fromEntries(
            dealFlows.map((x) => [x.id, x.name]),
          )}
          name="deal_flow"
          label="Dealflow"
        />
        <DictionaryField
          optionsDictionary={Object.fromEntries(
            dealStatuses.map((x) => [x.code, x.long]),
          )}
          name="investment_status"
          label="Investment Status"
        />
        {initialValues?.previous_snapshot && (
          <>
            <Paper elevation={3} sx={{ p: 1 }}>
              <StyledExtraActionTitle mt={1} mb={1}>
                Previous comment
              </StyledExtraActionTitle>
              <StyledExtraAction mb={1}>
                {initialValues.previous_snapshot.comment}
              </StyledExtraAction>
            </Paper>
          </>
        )}
        <RHFTextField
          name="comment"
          label="Comment"
          fullWidth
          multiline
          maxRows={8}
        />
        <CommentSummaryField
          name="comment_summary"
          label="Comment summary"
          source="comment"
          fullWidth
          multiline
          maxRows={8}
        />
        <RHFTextField
          name="comment_investment"
          label="Comment investment (previously #invest)"
          fullWidth
          multiline
          maxRows={8}
        />
        <RHFTextField
          name="reason_to_close"
          label="Reason to close"
          fullWidth
          multiline
          maxRows={8}
        />
        <TagField
          name="reason_to_close_tag"
          label="Reason to close tags"
          tagTypeId="TAG_REASON_TO_CLOSE"
          multiple
        />
        <DictionaryField
          optionsDictionary={Object.fromEntries(
            dealInvestmentRounds.map((x) => [x.value, x.label]),
          )}
          name="investment_round"
          label="Investment round"
        />
        <RHFTextField
          type="number"
          name="requested_amount"
          label="Requested amount"
          fullWidth
        />
        <RHFTextField
          type="number"
          name="reference_revenue"
          label="Reference revenue"
          fullWidth
        />
        <RHFTextField
          type="number"
          name="reference_revenue_year"
          label="Reference revenue year"
          fullWidth
        />
        <Box>
          <RHFCheckbox name="profitable" label="Profitable" nullable />
        </Box>
        <Box>
          <RHFCheckbox
            name="majority_owned_by_founders"
            label="Majority owned by founders"
            nullable
          />
        </Box>
        <Box>
          <RHFCheckbox name="has_fundraiser" label="Has fundraiser" nullable />
        </Box>
        {has_fundraiser === true && (
          <RHFTextField
            name="intermediary"
            label="Fundraiser"
            fullWidth
            multiline
          />
        )}
        <DictionaryField
          optionsDictionary={Object.fromEntries(
            dealWomanAmongFounders.map((x) => [x.value, x.label]),
          )}
          name="woman_among_founders"
          label="Woman among founders"
        />
        <TeamSourceField name="team_sources_ids" label="Team source" />
        <DictionaryField
          optionsDictionary={Object.fromEntries(
            dealSources.map((x) => [x.value, x.label]),
          )}
          name="deal_source"
          label="Deal source"
        />
        <PeopleField
          name="contributor_people"
          label="Contributors"
          multiple
          creatable
        />
        {company && <FinancialsWidget companyId={company} />}
        {deal && <FooterFormDates object={deal} />}
        <Box height={80} />
      </Stack>
      <FormButtons />
    </Box>
  );
};

const DealForm = (props) => {
  return (
    <Form
      validationSchema={dealValidationSchema}
      prepareInitialValues={getInitialValues}
      {...props}
    >
      <DealFormFields />
    </Form>
  );
};

export const ModelDealForm = buildGenericModelForm({
  form: DealForm,
  useGet: useDeal,
  useCreate: useCreateDeal,
  useUpdate: useUpdateDeal,
  useDelete: useDeleteDeal,
});
